import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import Navbar from "../components/Navbar";

const contactMethods = [
  {
    icon: "📧",
    title: "Email Architecture",
    content: "labdevelopers2025@gmail.com",
    link: "mailto:labdevelopers2025@gmail.com",
    description: "Transmit formal project specifications."
  },
  {
    icon: "📱",
    title: "WhatsApp Protocol",
    content: "+94 723 954 658",
    link: "https://wa.me/94723954658",
    description: "Instantaneous technical consultation."
  }
];

const serviceInquiries = [
  { value: "web-development", label: "Enterprise Web Development" },
  { value: "mobile-development", label: "Mobile App Development" },
  { value: "ai-ml-solutions", label: "AI & Machine Learning Solutions" },
  { value: "documentation", label: "Technical Documentation Services" },
  { value: "consultation", label: "Technology Consultation" },
  { value: "other", label: "Other Services" }
];

const budgetRanges = [
  { value: "under-5k", label: "Under $5,000" },
  { value: "5k-15k", label: "$5,000 - $15,000" },
  { value: "15k-50k", label: "$15,000 - $50,000" },
  { value: "discuss", label: "Project Specific" }
];

const faqs = [
  {
    question: "What is the typical deployment cycle?",
    answer: "Deployment cycles vary based on complexity. Simple websites take 2-4 weeks, while complex enterprise applications can take 3-6 months. We provide detailed timelines during consultation."
  },
  {
    question: "Do you provide infrastructure maintenance?",
    answer: "Yes, we offer comprehensive support packages including maintenance, updates, and 24/7 technical support. Support terms are included in all our service packages."
  },
  {
    question: "Can you modernize legacy architectures?",
    answer: "Absolutely! We specialize in integrating with existing systems and can help modernize legacy applications while maintaining industrial-grade continuity."
  }
];

export default function Contact() {
  const [formData, setFormData] = useState({
    firstName: "", lastName: "", email: "", phone: "", company: "",
    serviceType: "", budget: "", timeline: "", subject: "", message: ""
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);
  const [activeFaq, setActiveFaq] = useState(null);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus(null);
    try {
      await new Promise(resolve => setTimeout(resolve, 2000));
      setSubmitStatus("success");
      setFormData({ firstName: "", lastName: "", email: "", phone: "", company: "", serviceType: "", budget: "", timeline: "", subject: "", message: "" });
    } catch (error) {
      setSubmitStatus("error");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#020617] text-slate-100 selection:bg-blue-500/10 overflow-x-hidden font-inter">
      <Navbar />

      {/* Background Graphics */}
      <div className="fixed inset-0 bg-grid opacity-10 pointer-events-none" />
      <div className="fixed top-0 right-0 w-[800px] h-[800px] bg-blue-500/5 blur-[120px] rounded-full -mr-48 -mt-48 animate-blob" />
      <div className="fixed bottom-0 left-0 w-[600px] h-[600px] bg-indigo-500/5 blur-[120px] rounded-full -ml-32 -mb-32 animate-blob delay-2000" />
      <div className="fixed inset-0 bg-mesh opacity-20 pointer-events-none" />

      {/* Hero Section */}
      <section className="relative pt-64 pb-32 px-6 flex flex-col items-center text-center overflow-hidden">
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 1.2, ease: "easeOut" }}
          className="relative mb-16"
        >
          <div className="absolute -inset-14 bg-blue-500/10 blur-[130px] rounded-full pointer-events-none" />
          <h1 className="text-5xl md:text-8xl lg:text-[10.5rem] font-black text-white mb-10 tracking-[0.02em] leading-[0.85] relative z-10 uppercase font-inter px-4">
            Contact <br /><span className="text-gradient from-blue-600 via-slate-600 to-indigo-600">Architects.</span>
          </h1>
        </motion.div>

        <p className="text-lg md:text-2xl lg:text-3xl text-slate-400 max-w-4xl mb-20 leading-relaxed relative z-10 font-medium lowercase tracking-tight px-6">
          ready to transform your vision into an enterprise-grade reality?
          connect with our engineering leads for a high-impact strategy session.
        </p>
      </section>

      {/* Contact Cards */}
      <section className="py-32 px-6 relative z-10 bg-[#f1f5f9]/40 border-y border-white/5">
        <div className="container mx-auto max-w-7xl">
          <div className="flex flex-wrap justify-center gap-12">
            {contactMethods.map((method, idx) => (
              <motion.div
                key={method.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: idx * 0.1, duration: 0.6 }}
                whileHover={{ rotateY: 5, rotateX: -2, scale: 1.05 }}
                style={{ perspective: 1000 }}
                className="group glass-card p-10 md:p-14 rounded-[3rem] md:rounded-[4.5rem] border border-white/80 transition-all duration-700 hover:shadow-2xl hover:bg-slate-900/40/95 glow-border cursor-pointer w-full md:w-[calc(50%-1.5rem)] lg:w-[calc(33.333%-2rem)]"
                onClick={() => method.link.startsWith('http') ? window.open(method.link, '_blank') : window.location.href = method.link}
              >
                <div className="w-16 h-16 md:w-20 md:h-20 bg-slate-900/40 text-4xl md:text-5xl mb-12 flex items-center justify-center rounded-2xl md:rounded-3xl border border-white/5 group-hover:scale-110 group-hover:rotate-6 transition-all duration-700 shadow-sm">
                  {method.icon}
                </div>
                <h3 className="text-lg md:text-xl font-black text-white mb-4 uppercase tracking-tighter leading-none">{method.title}</h3>
                <p className="text-blue-600 font-black text-[10px] md:text-[11px] mb-8 truncate uppercase tracking-[0.2em]">{method.content}</p>
                <p className="text-slate-400 text-[9px] md:text-[10px] leading-relaxed font-black uppercase tracking-[0.3em]">{method.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Form Section */}
      <section className="py-40 px-6 relative z-10 bg-slate-900/40">
        <div className="container mx-auto max-w-7xl">
          <div className="grid lg:grid-cols-2 gap-24 items-start">
            {/* Form Column */}
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
              className="glass-card p-16 md:p-20 rounded-[5rem] border border-white/80 shadow-2xl shadow-slate-200/50"
            >
              <h2 className="text-4xl md:text-5xl font-black text-white mb-16 uppercase tracking-tighter leading-none">Submit <br />Protocol.</h2>
              <form onSubmit={handleSubmit} className="space-y-12">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-10">
                  <div className="space-y-4">
                    <label className="text-[10px] uppercase font-black text-slate-400 tracking-[0.4em] ml-4">Identity First</label>
                    <input
                      name="firstName"
                      value={formData.firstName}
                      onChange={handleChange}
                      className="w-full bg-[#020617] border border-white/5 rounded-[2.5rem] px-8 md:px-10 py-5 md:py-6 text-white focus:outline-none focus:border-blue-500 focus:bg-slate-900/40 transition-all font-medium placeholder:text-slate-300"
                      placeholder="Elon"
                    />
                  </div>
                  <div className="space-y-4">
                    <label className="text-[10px] uppercase font-black text-slate-400 tracking-[0.4em] ml-4">Identity Last</label>
                    <input
                      name="lastName"
                      value={formData.lastName}
                      onChange={handleChange}
                      className="w-full bg-[#020617] border border-white/5 rounded-[2.5rem] px-8 md:px-10 py-5 md:py-6 text-white focus:outline-none focus:border-blue-500 focus:bg-slate-900/40 transition-all font-medium placeholder:text-slate-300"
                      placeholder="Musk"
                    />
                  </div>
                </div>

                <div className="space-y-4">
                  <label className="text-[10px] uppercase font-black text-slate-400 tracking-[0.4em] ml-4">Access Network</label>
                  <input
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleChange}
                    className="w-full bg-[#020617] border border-white/5 rounded-[2.5rem] px-10 py-6 text-white focus:outline-none focus:border-blue-500 focus:bg-slate-900/40 transition-all font-medium placeholder:text-slate-300"
                    placeholder="architect@enterprise.com"
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-10">
                  <div className="space-y-4">
                    <label className="text-[10px] uppercase font-black text-slate-400 tracking-[0.4em] ml-4">Inquiry Type</label>
                    <div className="relative">
                      <select
                        name="serviceType"
                        value={formData.serviceType}
                        onChange={handleChange}
                        className="w-full bg-[#020617] border border-white/5 rounded-[2.5rem] px-8 md:px-10 py-5 md:py-6 text-white focus:outline-none focus:border-blue-500 focus:bg-slate-900/40 transition-all appearance-none cursor-pointer font-medium"
                      >
                        <option value="">Select Project</option>
                        {serviceInquiries.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
                      </select>
                      <div className="absolute right-8 top-1/2 -translate-y-1/2 pointer-events-none text-slate-400">↓</div>
                    </div>
                  </div>
                  <div className="space-y-4">
                    <label className="text-[10px] uppercase font-black text-slate-400 tracking-[0.4em] ml-4">Scale Range</label>
                    <div className="relative">
                      <select
                        name="budget"
                        value={formData.budget}
                        onChange={handleChange}
                        className="w-full bg-[#020617] border border-white/5 rounded-[2.5rem] px-8 md:px-10 py-5 md:py-6 text-white focus:outline-none focus:border-blue-500 focus:bg-slate-900/40 transition-all appearance-none cursor-pointer font-medium"
                      >
                        <option value="">Select Scale</option>
                        {budgetRanges.map(b => <option key={b.value} value={b.value}>{b.label}</option>)}
                      </select>
                      <div className="absolute right-8 top-1/2 -translate-y-1/2 pointer-events-none text-slate-400">↓</div>
                    </div>
                  </div>
                </div>

                <div className="space-y-4">
                  <label className="text-[10px] uppercase font-black text-slate-400 tracking-[0.4em] ml-4">Executive Brief</label>
                  <textarea
                    name="message"
                    rows="6"
                    value={formData.message}
                    onChange={handleChange}
                    className="w-full bg-[#020617] border border-white/5 rounded-[3rem] px-10 py-8 text-white focus:outline-none focus:border-blue-500 focus:bg-slate-900/40 transition-all resize-none font-medium placeholder:text-slate-300"
                    placeholder="describe your architectural requirements..."
                  />
                </div>

                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="group relative w-full py-7 bg-slate-900 text-white font-black rounded-[2.5rem] overflow-hidden transition-all shadow-2xl active:scale-95 disabled:opacity-50"
                >
                  <div className="absolute inset-0 bg-gradient-to-r from-blue-600 to-indigo-600 opacity-0 group-hover:opacity-100 transition-opacity" />
                  <span className="relative z-10 flex items-center justify-center gap-4 uppercase tracking-[0.2em] text-[11px]">
                    {isSubmitting ? "INITIATING UPLOAD..." : "TRANSMIT INQUIRY"}
                    <span className="text-xl">→</span>
                  </span>
                </button>
                <AnimatePresence>
                  {submitStatus === "success" && (
                    <motion.p
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      className="text-center text-blue-600 font-black text-[11px] tracking-[0.3em] uppercase pt-4"
                    >
                      Protocol Synchronized Successfully
                    </motion.p>
                  )}
                </AnimatePresence>
              </form>
            </motion.div>

            {/* Info Column */}
            <div className="space-y-24 pt-12">
              <div>
                <h2 className="text-4xl font-black text-white mb-16 uppercase tracking-tighter leading-none">Protocol <br />FAQ.</h2>
                <div className="space-y-8">
                  {faqs.map((faq, idx) => (
                    <div
                      key={idx}
                      className="glass-card rounded-[3rem] overflow-hidden border border-white/80 transition-all duration-500 hover:bg-slate-900/40"
                    >
                      <button
                        onClick={() => setActiveFaq(activeFaq === idx ? null : idx)}
                        className="w-full px-12 py-10 text-left flex justify-between items-center transition-colors group"
                      >
                        <span className="font-black text-white text-base uppercase tracking-tight group-hover:text-blue-600 transition-colors leading-none">{faq.question}</span>
                        <div className={`w-8 h-8 rounded-full border border-white/5 flex items-center justify-center transition-all duration-500 ${activeFaq === idx ? 'rotate-180 bg-blue-600 border-blue-600 text-white shadow-lg' : 'bg-slate-900/40'}`}>
                          <span className="text-sm">↓</span>
                        </div>
                      </button>
                      <AnimatePresence>
                        {activeFaq === idx && (
                          <motion.div
                            initial={{ height: 0, opacity: 0 }}
                            animate={{ height: "auto", opacity: 1 }}
                            exit={{ height: 0, opacity: 0 }}
                            className="overflow-hidden"
                          >
                            <div className="px-12 pb-12 text-sm text-slate-400 font-medium leading-relaxed lowercase">
                              {faq.answer}
                            </div>
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </div>
                  ))}
                </div>
              </div>

              <div className="glass-card p-16 rounded-[4.5rem] border border-white/80 shadow-xl shadow-slate-100/50">
                <h3 className="text-2xl font-black text-white mb-12 uppercase tracking-tighter leading-none">Industrial <br />Standards.</h3>
                <div className="grid grid-cols-2 gap-12">
                  {[
                    { label: "Capacity", val: "Industrial Scale" },
                    { label: "Sync Zone", val: "UTC+5:30" },
                    { label: "Governance", val: "ISO Certified" },
                    { label: "Integrity", val: "Bank-Level" }
                  ].map((stat, i) => (
                    <div key={i} className="space-y-2">
                      <label className="text-[9px] uppercase font-black text-blue-600 tracking-[0.4em] block">{stat.label}</label>
                      <span className="text-xs font-black text-slate-400 uppercase tracking-widest">{stat.val}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <footer className="py-24 px-6 border-t border-white/5 bg-[#f8fafc] text-center">
        <p className="text-[10px] font-black text-slate-300 uppercase tracking-[0.5em]">© 2024 LAB Developers — Protocol Headquarters.</p>
      </footer>
    </div>
  );
}
